/**
 * Export-Voreinstellungen für den KI-Hinweis ("Mit KI erstellt").
 *
 * Alle Export-Oberflächen (Projekt-Download, Workspace-Export, Assistent)
 * teilen sich dieselbe Wahl: einmal gesetzt, gilt sie überall weiter.
 *
 * localStorage (nicht sessionStorage): die Entscheidung soll Tabs und
 * Sitzungen überdauern — sie ist eine Kanzlei-/Makler-Präferenz, keine
 * Fortschritts-Session. Ist der Storage nicht verfügbar (Privacy-Modus),
 * fallen beide Werte still auf die Defaults zurück: Hinweis an, unten rechts.
 */

import type { LogoPosition } from '@/types';

/** Der Hinweis nutzt dieselben vier Ecken wie das Logo-Branding. */
export type AiLabelPosition = LogoPosition;

export const AI_LABEL_ENABLED_KEY = 'estage_ai_label_enabled';
export const AI_LABEL_POSITION_KEY = 'estage_ai_label_position';
export const AI_LABEL_OPACITY_KEY = 'estage_ai_label_opacity';

export const DEFAULT_AI_LABEL_POSITION: AiLabelPosition = 'bottom-right';

/** Deckkraft als Faktor auf den fertigen Badge: 1.0 = unveränderte Optik. */
export const DEFAULT_AI_LABEL_OPACITY = 1.0;
export const MIN_AI_LABEL_OPACITY = 0.3;
export const MAX_AI_LABEL_OPACITY = 1.0;

/** Ecken-Auswahl für die Export-Oberflächen (Optik wie der Logo-Picker). */
export const AI_LABEL_POSITIONS: { position: AiLabelPosition; label: string }[] = [
  { position: 'top-left', label: 'Oben links' },
  { position: 'top-right', label: 'Oben rechts' },
  { position: 'bottom-left', label: 'Unten links' },
  { position: 'bottom-right', label: 'Unten rechts' },
];

const POSITION_VALUES: readonly AiLabelPosition[] = AI_LABEL_POSITIONS.map((p) => p.position);

/**
 * Sicherer Zugriff auf localStorage — in manchen Privacy-Modi wirft bereits
 * der Property-Zugriff (Pattern wie in wizardSession.ts).
 */
const safeStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

/** Voreingestellt an — der Hinweis ist der Standardfall, nicht die Ausnahme. */
export const loadAiLabelEnabled = (): boolean => {
  try {
    return safeStorage()?.getItem(AI_LABEL_ENABLED_KEY) !== 'false';
  } catch {
    return true;
  }
};

export const saveAiLabelEnabled = (enabled: boolean): void => {
  try {
    safeStorage()?.setItem(AI_LABEL_ENABLED_KEY, enabled ? 'true' : 'false');
  } catch {
    /* Storage nicht verfügbar — Auswahl gilt dann nur für diesen Export */
  }
};

export const loadAiLabelPosition = (): AiLabelPosition => {
  try {
    const raw = safeStorage()?.getItem(AI_LABEL_POSITION_KEY);
    return POSITION_VALUES.find((p) => p === raw) ?? DEFAULT_AI_LABEL_POSITION;
  } catch {
    return DEFAULT_AI_LABEL_POSITION;
  }
};

export const saveAiLabelPosition = (position: AiLabelPosition): void => {
  try {
    safeStorage()?.setItem(AI_LABEL_POSITION_KEY, position);
  } catch {
    /* siehe saveAiLabelEnabled */
  }
};

/** Hält die Deckkraft im erlaubten Bereich; alles Unlesbare fällt auf 1.0. */
export const clampAiLabelOpacity = (value: unknown): number => {
  const num = typeof value === 'string' ? Number(value) : typeof value === 'number' ? value : NaN;
  if (!Number.isFinite(num)) return DEFAULT_AI_LABEL_OPACITY;
  return Math.min(MAX_AI_LABEL_OPACITY, Math.max(MIN_AI_LABEL_OPACITY, num));
};

export const loadAiLabelOpacity = (): number => {
  try {
    const raw = safeStorage()?.getItem(AI_LABEL_OPACITY_KEY);
    return raw === null || raw === undefined ? DEFAULT_AI_LABEL_OPACITY : clampAiLabelOpacity(raw);
  } catch {
    return DEFAULT_AI_LABEL_OPACITY;
  }
};

export const saveAiLabelOpacity = (opacity: number): void => {
  try {
    safeStorage()?.setItem(AI_LABEL_OPACITY_KEY, String(clampAiLabelOpacity(opacity)));
  } catch {
    /* siehe saveAiLabelEnabled */
  }
};
